import { TypeDecoratedSubscriptionRecord } from '@/lib/api/subscriptionManager/decorators/decorateSubscriptions';
import {
  BILLING_STATUS,
  PROCESS_STATUS,
  SUBSCRIPTION_PAYMENT_PLAN,
  TypeProductDecoratedScheduledItem,
  TypeScheduleSectionRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import {
  TypeAddOnProduct,
  TypeBundleDiscount,
  TypePlayKitBundleOffers,
} from '@/lib/api/productCatalog/types';
import interpolate from '@/utils/translations/interpolate';
import {
  Language,
  SupportedLanguages,
} from '@/utils/translations/types/TypeLanguagesAndLocales';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import {
  TypePlayGeniusOrder,
  TypePlayGeniusOrderLineItem,
} from '@/lib/api/orderManager/types/TypePlayGeniusOrder';
import {
  buildQueryString,
  constructLegacyCustomerPortalLink,
  SUBSCRIPTION_SOURCE_UUID_KEY,
} from '@/utils/shopSubdomain';
import { TypeDecoratedUserRecord } from '@/lib/api/accountManager/decorators/decorateUser';
import {
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import { CLICKED_ITEM_TYPES } from '@/core/constants';
import { TypeBannerFields } from '@/core/generated-types';
import { Draft, produce } from 'immer';

interface TypeGroupDecoratedScheduledItem
  extends TypeProductDecoratedScheduledItem {
  isCurrentPlayKit?: boolean;
}

export function getVisibleScheduledItems(
  subscription: TypeDecoratedSubscriptionRecord | undefined,
): TypeGroupDecoratedScheduledItem[] {
  if (!subscription) {
    return [];
  }

  const currentPlayKitStatuses = new Set([
    PROCESS_STATUS.ERROR,
    PROCESS_STATUS.BILLING,
    PROCESS_STATUS.SHIPMENT_PREPARATION,
    PROCESS_STATUS.SHIPPING,
    PROCESS_STATUS.COMPLETED,
  ]);

  const currentPlayKit = subscription.scheduled_items.findLast((item) =>
    currentPlayKitStatuses.has(item.process_status),
  );

  const upcomingPlayKitStatuses = new Set([
    PROCESS_STATUS.AWAITING_PROCESS,
    PROCESS_STATUS.SKIP_REQUESTED,
  ]);

  const awaitingProcessPlayKits = subscription.scheduled_items.filter((item) =>
    upcomingPlayKitStatuses.has(item.process_status),
  );

  return [
    ...(currentPlayKit ? [{ ...currentPlayKit, isCurrentPlayKit: true }] : []),
    ...awaitingProcessPlayKits,
  ];
}

type TypeScheduledItemGroups = {
  currentGroup: TypeScheduledItemGroup | undefined;
  nextGroups: TypeScheduledItemGroup[] | undefined;
  upcomingGroups: TypeScheduledItemGroup[];
};

export type TypeScheduledItemGroup = {
  items: TypeScheduleDecorateScheduledItem[];
  userUuid: string;
  subscriptionUuid: string;
  subscriptionShippingAddressUuid: string;
  order?: TypePlayGeniusOrder | undefined;
  isUpcomingOrderPreview?: boolean;
  savingsText?: string | undefined;
  isPartialSub?: boolean;
  isRecurringPufSub: boolean;
  primaryPaymentMethodLocalizedLabel?: string;
  canSupportBooks: boolean;
  isGiftOrder?: boolean;
  isCurrentPlayKitGroup?: boolean;
};

export interface TypeScheduleDecorateScheduledItem
  extends TypeProductDecoratedScheduledItem {
  badgeText: string | undefined;
  isBooksAddonActive: boolean;
  isShipDateVisible: boolean;
  isShipDateEditable: boolean;
  isHero: boolean;
  isNextItemEligibleForPrepayExtension: boolean;
}

export function determineBadgeTextFromOrder(
  scheduledItem: TypeProductDecoratedScheduledItem,
  order: TypePlayGeniusOrder | undefined,
  t: TypePlayKitPageTranslations,
): string {
  if (scheduledItem.process_status === PROCESS_STATUS.SKIP_REQUESTED) {
    return t.skippedBadgeText;
  }

  const isPaygo = scheduledItem.billing_status === BILLING_STATUS.PAID;
  if (!order || isPaygo) return '';

  const onlyPlayKitLineItems = order.line_items.filter(
    (item) =>
      item.properties.find((property) => property.name === 'product_type')
        ?.value === 'Play Kits',
  );

  const kitCount = onlyPlayKitLineItems.length;
  const missingProductType = kitCount === 0;

  if (missingProductType) {
    return t.prepaid;
  }

  const isNotInPrepayBundle = kitCount === 1;

  if (isNotInPrepayBundle) {
    return '';
  }

  const { lineItemIdx } = findScheduledItemOrderLineItemAssociation(
    scheduledItem,
    onlyPlayKitLineItems,
  );

  return interpolate(t.bundleBadgeText, {
    kitNumber: lineItemIdx + 1,
    kitCount: onlyPlayKitLineItems.length,
  });
}

export type ScheduledItemOrderLineItemAssociation = {
  lineItemIdx: number;
  isScheduledItemOnOrder: boolean;
};

function findScheduledItemOrderLineItemAssociation(
  scheduledItem: TypeProductDecoratedScheduledItem,
  lineItems: TypePlayGeniusOrderLineItem[] | undefined,
): ScheduledItemOrderLineItemAssociation {
  const res = {
    lineItemIdx: -1,
    isScheduledItemOnOrder: false,
  };

  if (!lineItems) {
    return res;
  }

  lineItems.forEach((line_item, index) => {
    const matchingSku = scheduledItem.product?.variants?.find(
      (variant) => variant.sku === line_item.sku,
    );

    if (matchingSku) {
      res.isScheduledItemOnOrder = true;
      res.lineItemIdx = index;
    }
  });

  return res;
}

function areBooksSupportedForSubscriptionLanguage(
  subscription: TypeSubscriptionRecord,
) {
  const unsupportedLanguages = new Set([
    Language.Dutch,
    Language.French,
    Language.German,
    Language.Spanish,
  ]);

  const areBooksSupported = (section: TypeScheduleSectionRecord) => {
    const isUnsupportedLanguageSelected = unsupportedLanguages.has(
      section.subscription_preference?.selected_options
        ?.language as unknown as SupportedLanguages,
    );

    return !isUnsupportedLanguageSelected;
  };

  return subscription.schedule_sections.every(areBooksSupported);
}

export function canSupportBooks(subscription: TypeDecoratedSubscriptionRecord) {
  const region = process.env.NEXT_PUBLIC_REGION;
  const isEUStore = region === 'EU';
  const isAUStore = region === 'AU';

  const globalConditionCheck =
    !subscription.partial_subscription && !subscription.isBooksAddonActive;

  if (isAUStore) return false;
  if (isEUStore) {
    return (
      globalConditionCheck &&
      areBooksSupportedForSubscriptionLanguage(subscription)
    );
  }

  return globalConditionCheck;
}

export type CreateScheduledItemGroupProps = {
  subscription: TypeDecoratedSubscriptionRecord;
  t: TypePlayKitPageTranslations;
  bundleDiscountData: TypeBundleDiscount | undefined;
  draftOrderData: TypePlayGeniusOrder | undefined;
  ordersData: TypePlayGeniusOrder[] | undefined;
  userData: TypeDecoratedUserRecord;
};

export function createScheduledItemGroups({
  subscription,
  t,
  bundleDiscountData,
  draftOrderData,
  ordersData,
  userData,
}: CreateScheduledItemGroupProps) {
  const scheduledItems = getVisibleScheduledItems(subscription);
  const groups: TypeScheduledItemGroup[] = [];
  const firstAwaitingProcessItemIdx = scheduledItems.findIndex(
    (item) => item.process_status === PROCESS_STATUS.AWAITING_PROCESS,
  );
  const nextItemEligibleForPrepayExtension =
    getNextItemEligibleForPrepayExtension(subscription);

  scheduledItems.forEach((scheduledItem, index) => {
    const { isScheduledItemOnOrder: isScheduledItemOnDraftOrder } =
      findScheduledItemOrderLineItemAssociation(
        scheduledItem,
        draftOrderData?.line_items,
      );

    if (isScheduledItemOnDraftOrder) {
      const existingGroupIdx = groups.findIndex(
        (group) => !!group.isUpcomingOrderPreview,
      );
      if (existingGroupIdx !== -1) {
        addItemToExistingGroup(existingGroupIdx);
      } else {
        createNewGroupFromDraftOrder();
      }
    } else {
      // otherwise, utilize order data to group items
      const existingGroupIdx = groups.findIndex(
        (group) =>
          !!group.order?.playgenius_order_id &&
          group.order?.playgenius_order_id ===
            scheduledItem.playgenius_order_id,
      );

      if (existingGroupIdx !== -1) {
        addItemToExistingGroup(existingGroupIdx);
      } else {
        createNewGroup();
      }
    }

    function addItemToExistingGroup(groupIndex: number) {
      const existingItems = groups[groupIndex].items;
      groups[groupIndex].items = [
        ...existingItems,
        decorateItemWithOrderData(
          scheduledItem,
          groups[groupIndex].order,
          index,
        ),
      ];
    }

    function createNewGroupFromDraftOrder() {
      const newGroup: TypeScheduledItemGroup = {
        items: [
          decorateItemWithOrderData(scheduledItem, draftOrderData, index),
        ],
        order: draftOrderData,
        userUuid: userData.uuid,
        subscriptionUuid: subscription.uuid,
        subscriptionShippingAddressUuid: subscription.delivery_address_uuid,
        isUpcomingOrderPreview: true,
        primaryPaymentMethodLocalizedLabel:
          userData.primaryPaymentMethod?.localized_label,
        savingsText: bundleDiscountData
          ? interpolate(t.recurringPufSavings, {
              kitCount: bundleDiscountData.size,
              savingsPercentage: Math.ceil(bundleDiscountData.amount),
            })
          : '',
        isRecurringPufSub:
          subscription.paymentPlan === SUBSCRIPTION_PAYMENT_PLAN.REPUF,
        isPartialSub: subscription.partial_subscription,
        canSupportBooks: canSupportBooks(subscription),
      };

      groups.push(newGroup);
    }
    function createNewGroup() {
      const targetOrder = ordersData?.find(
        (order) =>
          order.playgenius_order_id === scheduledItem.playgenius_order_id,
      );

      const newGroup: TypeScheduledItemGroup = {
        order: targetOrder,
        userUuid: userData.uuid,
        subscriptionUuid: subscription.uuid,
        subscriptionShippingAddressUuid: subscription.delivery_address_uuid,
        items: [decorateItemWithOrderData(scheduledItem, targetOrder, index)],
        savingsText: '',
        isGiftOrder: false,
        isPartialSub: subscription.partial_subscription,
        isRecurringPufSub:
          subscription.paymentPlan === SUBSCRIPTION_PAYMENT_PLAN.REPUF,
        canSupportBooks: canSupportBooks(subscription),
        isCurrentPlayKitGroup: scheduledItem.isCurrentPlayKit,
      };

      // if we have an order associated with the group, identify puf savings if applicable
      if (targetOrder) {
        const { lineItemIdx } = findScheduledItemOrderLineItemAssociation(
          scheduledItem,
          targetOrder?.line_items,
        );

        const lineItemProperties =
          targetOrder?.line_items?.[lineItemIdx]?.properties;
        const savingsPercentage =
          lineItemProperties?.find(
            ({ name }) => name === 'puf_discount_display',
          )?.value ||
          lineItemProperties?.find(({ name }) => name === 'puf_discount')
            ?.value;

        const giftOrderCode = targetOrder?.line_items?.[
          lineItemIdx
        ]?.properties?.find(
          (property) => property.name === 'gifting_order_activation_code',
        )?.value;

        if (giftOrderCode) {
          newGroup.isGiftOrder = true;
        }

        if (
          !giftOrderCode &&
          savingsPercentage &&
          Number(savingsPercentage) > 0
        ) {
          newGroup.savingsText = interpolate(t.pufSavings, {
            savingsPercentage: Math.ceil(Number(savingsPercentage)),
          });
        }
      }

      groups.push(newGroup);
    }
  });

  function decorateItemWithOrderData(
    scheduledItem: TypeProductDecoratedScheduledItem,
    order: TypePlayGeniusOrder | undefined,
    index: number,
  ) {
    const isBooksAddonActive = () =>
      !!(
        subscription.isBooksAddonActive &&
        scheduledItem.scheduled_addons?.some(
          (item) => item.addon_kind === 'books_addon',
        )
      );

    const decoratedScheduledItem: TypeScheduleDecorateScheduledItem = {
      ...scheduledItem,
      isBooksAddonActive: isBooksAddonActive(),
      isShipDateEditable: index === firstAwaitingProcessItemIdx,
      isShipDateVisible: index > 0,
      isNextItemEligibleForPrepayExtension:
        scheduledItem.uuid === nextItemEligibleForPrepayExtension?.uuid,
      isHero: index === 0,
      badgeText: determineBadgeTextFromOrder(scheduledItem, order, t),
    };

    return decoratedScheduledItem;
  }

  let targetGroup, currentGroup;
  const nextGroups = [],
    upcomingGroups = [];

  for (let i = 0; i < groups.length; i++) {
    targetGroup = groups[i];
    if (targetGroup.isCurrentPlayKitGroup) {
      currentGroup = targetGroup;
      continue;
    }

    if (targetGroup.order) {
      nextGroups.push(targetGroup);
    } else {
      upcomingGroups.push(targetGroup);
    }
  }

  const resultingGroups: TypeScheduledItemGroups = {
    currentGroup,
    nextGroups,
    upcomingGroups,
  };

  return resultingGroups;
}

export type BookBundleUpsellProductDetails = {
  shouldShowBookBundleUpsell: boolean;
  bookBundleUpsellProducts: TypeAddOnProduct[];
};

export function getBookBundleUpsellProductDetails(
  group: TypeScheduledItemGroup,
): BookBundleUpsellProductDetails {
  if (!group.canSupportBooks) {
    return {
      shouldShowBookBundleUpsell: false,
      bookBundleUpsellProducts: [],
    };
  }

  const unprocessedItemsInGroup = group.items.filter(
    (item) => item.process_status === PROCESS_STATUS.AWAITING_PROCESS,
  );
  const upsellProducts = unprocessedItemsInGroup
    ?.map((item) => item.bookBundleProduct)
    .filter((product) => product !== undefined);

  return {
    shouldShowBookBundleUpsell:
      group.canSupportBooks && upsellProducts.length > 0,
    bookBundleUpsellProducts: upsellProducts,
  };
}

export function generateBookAddonLink(
  group: TypeScheduledItemGroup,
  locale: string,
): string {
  const queryParameters = [
    {
      key: SUBSCRIPTION_SOURCE_UUID_KEY,
      value: group.subscriptionUuid,
    },
  ];
  const queryString = buildQueryString(queryParameters);

  const link = constructLegacyCustomerPortalLink(
    locale,
    [
      group.userUuid,
      'subscriptions',
      group.subscriptionUuid,
      'add-ons',
      'books_addon',
    ],
    queryString,
  );
  return link;
}

// rudderstack click events for the PlayKitsPage
export const trackClickEvent = (cta: CTA_NAMES) => {
  trackItemClickedEvent({
    url: window.location.href,
    type: CLICKED_ITEM_TYPES.LINK,
    pageLocation: PAGE_LOCATIONS.PLAY_KITS_PAGE,
    cta: cta,
  });
};

export function getNextItemEligibleForPrepayExtension(
  subscription: TypeDecoratedSubscriptionRecord,
) {
  return subscription.scheduled_items.find((item) => {
    const itemPurchasable =
      item.billing_status === BILLING_STATUS.AWAITING_PAYMENT;
    const itemUnprocessed =
      item.process_status === PROCESS_STATUS.AWAITING_PROCESS;

    return itemPurchasable && itemUnprocessed;
  });
}

export function getPrepayBundleOffers(
  playKitBundlesData?: TypePlayKitBundleOffers,
) {
  if (!playKitBundlesData) return [];
  return playKitBundlesData?.applicable_bundles?.filter(
    (bundle) => bundle?.size > 1,
  );
}

export function getItemErrors(
  scheduledItem: TypeProductDecoratedScheduledItem,
  t: TypePlayKitPageTranslations,
) {
  const { billing_error_message, shipping_error_message } = scheduledItem;
  const errors = [];

  if (billing_error_message) {
    let billingErrorDescription = t.paymentMethodDefaultError;

    if (billing_error_message.includes('expired')) {
      billingErrorDescription = t.paymentMethodExpired;
    } else if (billing_error_message.includes('declined')) {
      billingErrorDescription = t.paymentMethodDeclined;
    }

    errors.push({
      title: t.paymentError,
      description: billingErrorDescription,
    });
  }

  if (shipping_error_message) {
    errors.push({
      title: t.shippingError,
      description: t.shippingDefaultError,
    });
  }

  return errors;
}

export type BannerOverrides = {
  linkUrl?: string;
  headingStringInterpolations?: { [key: string]: string | number };
};
export function buildBannerPropsWithDynamicContent(
  bannerFields: TypeBannerFields,
  { linkUrl, headingStringInterpolations }: BannerOverrides,
): TypeBannerFields {
  return produce(bannerFields, (draft: Draft<TypeBannerFields>) => {
    if (linkUrl && draft?.cta?.fields) {
      draft.cta.fields.linkUrl = linkUrl;
    }

    if (headingStringInterpolations) {
      draft.headerText = interpolate(
        bannerFields.headerText,
        headingStringInterpolations,
      );
    }
  });
}
